/**
 * LeoClinic — Tipos partilhados
 * @module @leoclinic/types
 */

/** Papéis de utilizador no sistema multi-clínica */
export type UserRole = 'administrator' | 'receptionist' | 'therapist' | 'client';

/** Estado genérico de registos */
export type EntityStatus = 'active' | 'inactive' | 'archived';

/** Perfil de utilizador (extends auth.users) */
export interface Profile {
  id: string;
  clinic_id: string | null;
  email: string;
  full_name: string;
  avatar_url: string | null;
  phone: string | null;
  role: UserRole;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Clínica (tenant) */
export interface Clinic {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
  logo_url: string | null;
  timezone: string;
  currency: string;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Funcionário da clínica */
export interface Employee {
  id: string;
  clinic_id: string;
  profile_id: string;
  job_title: string | null;
  hire_date: string | null;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Terapeuta */
export interface Therapist {
  id: string;
  clinic_id: string;
  employee_id: string;
  specialty: string | null;
  bio: string | null;
  skills: string[];
  photo_url: string | null;
  color: string | null;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Cliente da clínica */
export interface Client {
  id: string;
  clinic_id: string;
  profile_id: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  birth_date: string | null;
  photo_url: string | null;
  notes: string | null;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Estado de marcação */
export type AppointmentStatus =
  | 'pending'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show';

/** Item do histórico de marcações do cliente */
export interface ClientAppointmentHistory {
  id: string;
  start_at: string;
  end_at: string;
  status: AppointmentStatus;
  notes: string | null;
  treatment_id: string;
}
export interface Category {
  id: string;
  clinic_id: string;
  name: string;
  description: string | null;
  color: string | null;
  sort_order: number;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Tratamento com categoria expandida */
export type TreatmentWithCategory = Treatment & {
  category: Pick<Category, 'id' | 'name' | 'color'> | null;
};

/** Tratamento */
export interface Treatment {
  id: string;
  clinic_id: string;
  category_id: string | null;
  name: string;
  description: string | null;
  price: number;
  duration_minutes: number;
  image_url: string | null;
  color: string | null;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Marcação */
export interface Appointment {
  id: string;
  clinic_id: string;
  client_id: string;
  therapist_id: string;
  treatment_id: string;
  start_at: string;
  end_at: string;
  status: AppointmentStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Marcação expandida para calendário */
export interface AppointmentWithDetails extends Appointment {
  client_name: string;
  treatment_name: string;
  treatment_duration: number;
  therapist_name: string;
  therapist_color: string | null;
  treatment_color: string | null;
}

/** Evento do calendário FullCalendar */
export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  display?: 'auto' | 'block' | 'list-item' | 'background' | 'inverse-background' | 'none';
  extendedProps: {
    appointment: AppointmentWithDetails;
    isBlocked?: boolean;
  };
}

/** Dados formulário marcação */
export interface AppointmentFormData {
  client_id: string;
  therapist_id: string;
  treatment_id: string;
  start_at: string;
  end_at: string;
  status: AppointmentStatus;
  notes?: string | null;
}

/** Filtros da agenda */
export interface AgendaFilters {
  therapist_id?: string;
  start: string;
  end: string;
}

/** Estado de pagamento */
export type PaymentStatus = 'pending' | 'paid' | 'partial' | 'refunded';

/** Método de pagamento */
export type PaymentMethod = 'cash' | 'mbway' | 'card' | 'transfer' | 'stripe';

/** Pagamento */
export interface Payment {
  id: string;
  clinic_id: string;
  appointment_id: string | null;
  client_id: string;
  amount: number;
  method: PaymentMethod;
  status: PaymentStatus;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Métricas do dashboard */
export interface DashboardMetrics {
  appointmentsToday: number;
  revenueToday: number;
  revenueMonth: number;
  newClients: number;
  activeClients: number;
  occupancyRate: number;
}

/** Item de navegação da sidebar */
export interface NavItem {
  title: string;
  href: string;
  icon: string;
  roles?: UserRole[];
  badge?: string | number;
}

/** Sessão de autenticação */
export interface AuthSession {
  user: Profile;
  clinic: Clinic | null;
}

/** Resposta paginada genérica */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** Filtros de pesquisa genéricos */
export interface SearchFilters {
  query?: string;
  status?: EntityStatus;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/** Filtros específicos de clientes */
export interface ClientFilters extends SearchFilters {
  sortBy?: 'full_name' | 'created_at' | 'email';
}

/** Dados para criar/editar cliente */
export interface ClientFormData {
  full_name: string;
  email?: string | null;
  phone?: string | null;
  birth_date?: string | null;
  notes?: string | null;
  status?: EntityStatus;
  photo_url?: string | null;
}

/** Horário de trabalho */
export interface WorkingHour {
  id: string;
  clinic_id: string;
  therapist_id: string | null;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Período bloqueado (férias, almoço, etc.) */
export interface BlockedPeriod {
  id: string;
  clinic_id: string;
  therapist_id: string | null;
  start_at: string;
  end_at: string;
  reason: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Terapeuta com dados do perfil */
export interface TherapistWithProfile extends Therapist {
  full_name: string;
  email: string;
  phone: string | null;
}

/** Dados formulário categoria */
export interface CategoryFormData {
  name: string;
  description?: string | null;
  color?: string | null;
  sort_order?: number;
  status?: EntityStatus;
}

/** Dados formulário tratamento */
export interface TreatmentFormData {
  name: string;
  description?: string | null;
  price: number;
  duration_minutes: number;
  category_id?: string | null;
  color?: string | null;
  image_url?: string | null;
  status?: EntityStatus;
}

/** Dados formulário terapeuta */
export interface TherapistFormData {
  profile_id: string;
  specialty?: string | null;
  bio?: string | null;
  skills?: string[];
  color?: string | null;
  photo_url?: string | null;
  status?: EntityStatus;
}

/** Filtros de tratamentos */
export interface TreatmentFilters extends SearchFilters {
  category_id?: string;
  sortBy?: 'name' | 'price' | 'duration_minutes' | 'created_at';
}

/** Filtros de categorias */
export interface CategoryFilters extends SearchFilters {
  sortBy?: 'name' | 'sort_order' | 'created_at';
}

/** Dados formulário horário */
export interface WorkingHourFormData {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active?: boolean;
}

/** Dados formulário bloqueio */
export interface BlockedPeriodFormData {
  start_at: string;
  end_at: string;
  reason?: string | null;
}

/** Tema da aplicação */
export type ThemeMode = 'light' | 'dark' | 'system';

/** Cores da marca LeoClinic */
export const BRAND_COLORS = {
  greenDark: '#2F4F46',
  gold: '#C7A567',
  white: '#FFFFFF',
  cream: '#F6F3EE',
  gray: '#ECECEC',
} as const;

/** Estado lista de espera */
export type WaitlistStatus = 'waiting' | 'notified' | 'booked' | 'cancelled';

/** Entrada lista de espera */
export interface WaitlistEntry {
  id: string;
  clinic_id: string;
  client_id: string;
  treatment_id: string;
  therapist_id: string | null;
  preferred_date: string | null;
  notes: string | null;
  status: WaitlistStatus;
  notified_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface WaitlistEntryWithDetails extends WaitlistEntry {
  client_name: string;
  treatment_name: string;
  therapist_name: string | null;
}

export interface WaitlistFormData {
  client_id: string;
  treatment_id: string;
  therapist_id?: string | null;
  preferred_date?: string | null;
  notes?: string | null;
}

export interface PaymentWithDetails extends Payment {
  client_name: string;
  appointment_label: string | null;
}

export interface PaymentFormData {
  client_id: string;
  appointment_id?: string | null;
  amount: number;
  method: PaymentMethod;
  status?: PaymentStatus;
  notes?: string | null;
}

export interface PaymentFilters extends Omit<SearchFilters, 'status'> {
  status?: PaymentStatus;
  method?: PaymentMethod;
  from?: string;
  to?: string;
}

export interface AppointmentFilters extends Omit<SearchFilters, 'status'> {
  status?: AppointmentStatus;
  therapist_id?: string;
  from?: string;
  to?: string;
}

/** Canal de notificação */
export type NotificationChannel = 'email' | 'sms' | 'push' | 'in_app';

/** Estado campanha */
export type CampaignStatus = 'draft' | 'scheduled' | 'sent' | 'cancelled';

export interface Campaign {
  id: string;
  clinic_id: string;
  name: string;
  subject: string | null;
  content: string | null;
  channel: NotificationChannel;
  status: CampaignStatus;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface CampaignFormData {
  name: string;
  subject?: string | null;
  content?: string | null;
  channel: NotificationChannel;
  status?: CampaignStatus;
  scheduled_at?: string | null;
}

export interface Notification {
  id: string;
  clinic_id: string;
  profile_id: string;
  title: string;
  body: string | null;
  channel: NotificationChannel;
  is_read: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface GiftCard {
  id: string;
  clinic_id: string;
  code: string;
  amount: number;
  balance: number;
  expires_at: string | null;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface GiftCardFormData {
  code: string;
  amount: number;
  expires_at?: string | null;
}

export interface ReportFilters {
  from: string;
  to: string;
  therapist_id?: string;
}

export interface WeeklyRevenuePoint {
  day: string;
  revenue: number;
  appointments: number;
}

export interface ReportSummary {
  totalRevenue: number;
  totalAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  averageTicket: number;
  byMethod: { method: PaymentMethod; total: number }[];
  byStatus: { status: AppointmentStatus; count: number }[];
}

export interface AccountingPaymentLine {
  id: string;
  paid_at: string;
  client_name: string;
  amount: number;
  method: PaymentMethod;
  appointment_label: string | null;
  notes: string | null;
}

export interface AccountingReport {
  totalRevenue: number;
  paymentCount: number;
  excludedCashTotal: number;
  excludedCashCount: number;
  byMethod: { method: PaymentMethod; total: number; count: number }[];
  lines: AccountingPaymentLine[];
}

export interface ClinicalSummary {
  id: string;
  clinic_id: string;
  client_id: string;
  appointment_id: string | null;
  summary: string;
  generated_at: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface AiSuggestion {
  id: string;
  clinic_id: string;
  entity_type: string;
  entity_id: string;
  suggestion: string;
  accepted: boolean | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface BookingFormData {
  client_name: string;
  client_email: string;
  client_phone: string;
  treatment_id: string;
  therapist_id: string;
  start_at: string;
  notes?: string | null;
}

export interface AvailableSlot {
  start_at: string;
}

/** Perfil público da clínica (portal cliente / marcação online) */
export interface PublicProfileSettings {
  tagline?: string;
  description?: string;
  facebook_url?: string;
  instagram_url?: string;
  whatsapp?: string;
  highlights?: string[];
}

export interface PublicClinicView {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  profile: PublicProfileSettings;
  hoursLabel: string;
  /** Logo personalizado (Definições admin) — null se usar logo por defeito */
  logoUrl: string | null;
}

export interface ClinicSettings {
  business_hours?: Record<string, { open: string; close: string; closed?: boolean } | null>;
  appointment_settings?: {
    min_advance_hours: number;
    max_advance_days: number;
    cancellation_hours: number;
  };
  notifications?: {
    email_reminder_hours: number;
    sms_reminder_hours: number;
  };
  public_profile?: PublicProfileSettings;
  /** Logo da app admin (Leo Suite) — URL pública */
  app_branding?: {
    logo_url?: string | null;
  };
}

export interface DashboardExtendedMetrics extends DashboardMetrics {
  weeklyRevenue: WeeklyRevenuePoint[];
  recentAppointments: AppointmentWithDetails[];
  recentClients: Client[];
}
